import cn from 'classnames';
import type { FC } from 'react';
import React from 'react';

import { Badge, Divider, Space, Typography } from '@sravni/react-design-system';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import { FULL_DEAL_FORM_AMOUNT_DEFAULT, FULL_DEAL_FORM_TERM_DEFAULT } from 'entities/FullDeal/config';
import { useMicrocreditDeals } from 'entities/FullDeal/lib';
import FullDealCoolingOffAlert from 'entities/FullDeal/ui/FullDealCoolingOffAlert/FullDealCoolingOffAlert';
import { CardLogo } from 'entities/Microcredits/ui/LightweightCard/components';
import { FullDealEntryPointForm } from 'features/FullDeal';

import { useOnChangeCalculator } from '../../lib';
import type { FullDealEntryPointProps } from '../FullDealEntryPoint/FullDealEntryPoint';

import styles from './FullDealEntryPointCompose.module.scss';

export const FullDealEntryPointCompose: FC<FullDealEntryPointProps & { offer: IClientMicrocreditListItem }> = ({
  highlightOffer = true,
  shouldSetDefaultValuesInRedux = true,
  renderActions,
  offer,
}) => {
  const { initialAmount, initialTerm } = useMicrocreditDeals({
    initialAmount: FULL_DEAL_FORM_AMOUNT_DEFAULT,
    initialTerm: FULL_DEAL_FORM_TERM_DEFAULT,
  });

  const { amount, term, handleChange } = useOnChangeCalculator({
    initialAmount,
    initialTerm: initialTerm as number,
    shouldSetDefaultValuesInRedux,
  });

  return (
    <div className={styles.relativeContainer}>
      <div
        className={cn(styles.container, {
          [styles.withOfferOrForm]: highlightOffer,
        })}
      >
        <Space direction="column">
          <div className={styles.headerConstructContainer}>
            <Space direction="column" size={8}>
              {offer.seoLabel?.length && (
                <Badge variant="primary" color="green" shape="round" className={styles.badge}>
                  {offer.seoLabel}
                </Badge>
              )}
              <Typography.Heading className={styles.text} level={3} as="div">
                Займ в {offer.organization.name}
              </Typography.Heading>
            </Space>
            <div>
              <CardLogo
                logo={offer.organization?.logotypes?.android || offer.organization?.logotypes?.square}
                isPartner={offer.isBankPartner || offer.advertising?.isPartner}
              />
            </div>
          </div>
          <div className={cn(styles.content)}>
            <FullDealEntryPointForm amount={amount} term={term} onChange={handleChange} />
            {amount >= 50_000 && <FullDealCoolingOffAlert />}
          </div>
        </Space>

        <Space size={16} direction="vertical" align="center" className={styles.box}>
          <Divider className={styles.divider} />
          {renderActions?.({
            amount,
            term,
            toFullDealButtonText: '',
            hasOffer: false,
          })}
        </Space>
      </div>
    </div>
  );
};
